<template>
  <div class="garmin-activity-detail">
    <div class="d-flex align-center justify-end ga-2 mb-4">
      <v-btn
        v-if="isCyclingActivity(props.session)"
        :to="`/sessions/${props.session.id}/virtual-elevation`"
        color="teal"
        variant="flat"
        prepend-icon="mdi-chart-areaspline"
      >
        Virtual Elevation Analysis
      </v-btn>
      <v-btn
        variant="outlined"
        size="small"
        prepend-icon="mdi-refresh"
        :loading="recalculating"
        @click="recalculate"
      >
        Recalculate metrics
      </v-btn>
    </div>

    <!-- Row 1: Metric Groups -->
    <v-row class="mb-6">
      <v-col cols="12" sm="6" md="4" v-for="group in metricGroups" :key="group.title">
        <v-card class="rounded-xl border h-100" elevation="1">
          <v-card-title class="text-subtitle-1 font-weight-black text-secondary d-flex align-center">
            <v-icon :color="group.color" class="me-2">{{ group.icon }}</v-icon>
            {{ group.title.toUpperCase() }}
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text>
            <div
              v-for="row in group.rows"
              :key="row.label"
              class="d-flex align-center justify-space-between py-1"
            >
              <span class="text-caption font-weight-medium text-grey-darken-1 text-uppercase">{{ row.label }}</span>
              <div class="d-flex align-center">
                <span class="text-body-1 font-weight-black text-secondary">{{ row.value }}</span>
                <v-chip
                  v-if="row.source === 'computed'"
                  size="x-small"
                  color="blue-grey"
                  variant="tonal"
                  class="text-caption ms-2"
                >
                  Est.
                </v-chip>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Row 2: Map & Lap Splits -->
    <v-row class="mb-6">
      <!-- Route Map -->
      <v-col cols="12" md="7">
        <v-card class="rounded-xl border h-100" elevation="1">
          <v-card-title class="text-subtitle-1 font-weight-black text-secondary d-flex align-center">
            <v-icon color="primary" class="me-2">mdi-map-marker-path</v-icon>
            ROUTE MAP
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text class="pa-0 relative" style="height: 400px; min-height: 400px;">
            <div id="garmin-map" class="w-100 h-100" style="z-index: 1;"></div>
            <div v-if="!hasCoordinates" class="absolute-center text-center text-medium-emphasis">
              <v-icon size="large" class="mb-2">mdi-map-off</v-icon>
              <div>No GPS coordinates recorded for this activity.</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Lap Splits -->
      <v-col cols="12" md="5">
        <v-card class="rounded-xl border h-100" elevation="1">
          <v-card-title class="text-subtitle-1 font-weight-black text-secondary d-flex align-center">
            <v-icon color="primary" class="me-2">mdi-format-list-numbered</v-icon>
            LAP SPLITS ({{ isComputedSplits ? '1 KM Interval Splits' : 'Device Laps' }})
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text class="pa-0">
            <v-table class="elevation-0 w-100" density="compact" fixed-header style="height: 400px; overflow-y: auto;">
              <thead>
                <tr>
                  <th class="font-weight-bold text-caption text-uppercase">Lap</th>
                  <th class="font-weight-bold text-caption text-uppercase">Time</th>
                  <th class="font-weight-bold text-caption text-uppercase">Dist (km)</th>
                  <th class="font-weight-bold text-caption text-uppercase">Avg Speed</th>
                  <th class="font-weight-bold text-caption text-uppercase">Avg Power</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="lap in displayLaps" :key="lap.lapIndex">
                  <td class="font-weight-black text-caption text-secondary">#{{ lap.lapIndex }}</td>
                  <td>{{ formatDuration(lap.totalTime) }}</td>
                  <td>{{ (lap.distance / 1000).toFixed(2) }}</td>
                  <td>{{ Number(lap.avgSpeed).toFixed(1) }} km/h</td>
                  <td>{{ Math.round(lap.avgPower) }} W</td>
                </tr>
                <tr v-if="displayLaps.length === 0">
                  <td colspan="5" class="text-center text-medium-emphasis py-8">
                    No splits data available.
                  </td>
                </tr>
              </tbody>
            </v-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Row 3: Telemetry Graph -->
    <v-row class="mb-6">
      <v-col cols="12">
        <v-card class="rounded-xl border" elevation="1">
          <v-card-title class="text-subtitle-1 font-weight-black text-secondary d-flex align-center">
            <v-icon color="primary" class="me-2">mdi-chart-line</v-icon>
            TELEMETRY TIMELINE
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text>
            <VueApexCharts
              v-if="chartSeries[0].data.length > 0"
              type="line"
              height="350"
              :options="chartOptions"
              :series="chartSeries"
            ></VueApexCharts>
            <div v-else class="text-center text-medium-emphasis py-8">
              No telemetry timeline samples recorded.
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, nextTick } from 'vue';
import VueApexCharts from 'vue3-apexcharts';
import * as L from 'leaflet';
import { functions } from '@/js/firebase';
import { httpsCallable } from 'firebase/functions';
import { formatPaceFromSeconds } from '@/utils/pace';
import { isCyclingActivity } from '@/utils/sessionParams';

interface Lap {
  lapIndex: number;
  totalTime: number;
  distance: number;
  avgSpeed: number;
  avgPower: number;
  avgHeartRate?: number | null;
  avgCadence?: number | null;
  calories?: number | null;
}

interface Sample {
  timestamp: number;
  lat: number | null;
  lng: number | null;
  speed: number;
  power: number;
  heartRate: number | null;
  cadence: number | null;
  garminDist: number;
  altitude: number | null;
  grade: number | null;
}

type MetricSource = 'garmin' | 'computed';

interface GarminSession {
  id: string;
  name?: string;
  sessionName?: string;
  distance?: number; // metres
  totalMass?: number; // rider + bike mass in kg — NOT a distance
  timestamp?: any;
  startTime?: any;
  samples?: Sample[];
  laps?: Lap[];
  source: string;
  // Server-computed advanced metrics (garmin.ts / recalculateGarminActivity) --
  // see garminMetrics.ts for field naming (avoids TrainingPeaks trademarked terms).
  movingTime?: number;
  elapsedTime?: number;
  weightedAvgPower?: number;
  trainingLoad?: number;
  intensityRatio?: number;
  maxSpeed?: number;
  avgHeartRate?: number;
  maxHeartRate?: number;
  minHeartRate?: number;
  avgCadence?: number;
  maxCadence?: number;
  calories?: number;
  totalAscent?: number;
  totalDescent?: number;
  avgGrade?: number;
  sport?: string | null;
  normalizedGradedPace?: number; // seconds/km, running only -- see garminMetrics.ts computeNormalizedGradedPace
  metricsSource?: Partial<Record<string, MetricSource>>;
}

const props = defineProps<{
  session: GarminSession;
}>();

// Overlaid onto props.session after a successful recalculation, so the UI
// reflects the refreshed values without waiting for the parent to refetch.
const recalculatedMetrics = ref<Partial<GarminSession> | null>(null);
const effectiveSession = computed<GarminSession>(() => ({
  ...props.session,
  ...(recalculatedMetrics.value || {})
}));

const recalculating = ref(false);
async function recalculate() {
  recalculating.value = true;
  try {
    const recalculateFn = httpsCallable(functions, 'recalculateGarminActivity');
    const response = await recalculateFn({ sessionId: props.session.id });
    const data = response.data as { metrics: Partial<GarminSession>; metricsSource: Partial<Record<string, MetricSource>> };
    recalculatedMetrics.value = { ...data.metrics, metricsSource: data.metricsSource };
  } catch (error: any) {
    console.error('recalculateGarminActivity failed:', error);
  } finally {
    recalculating.value = false;
  }
}

const hasCoordinates = ref(false);
const isComputedSplits = ref(false);
let map: L.Map | null = null;

// Helpers
const formatDuration = (seconds: number): string => {
  if (!seconds || seconds <= 0) return '0:00';
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  if (hrs > 0) {
    return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

// Computed Laps (dynamically builds 1km splits if device laps are absent)
const displayLaps = computed<Lap[]>(() => {
  if (props.session.laps && props.session.laps.length > 0) {
    isComputedSplits.value = false;
    return props.session.laps;
  }

  const samples = props.session.samples || [];
  if (samples.length === 0) return [];

  isComputedSplits.value = true;
  const computedLaps: Lap[] = [];
  let currentLapIndex = 1;
  let lastLapDist = 0;
  let lastLapTime = samples[0].timestamp;

  let segmentPowerSum = 0;
  let segmentSpeedSum = 0;
  let segmentCount = 0;

  for (let i = 0; i < samples.length; i++) {
    const s = samples[i];
    const dist = s.garminDist || 0;

    segmentPowerSum += s.power || 0;
    segmentSpeedSum += s.speed || 0;
    segmentCount++;

    const isLastSample = i === samples.length - 1;
    const distanceDelta = dist - lastLapDist;

    // Split every 1000m (1 km) or at the end of the ride
    if (distanceDelta >= 1000 || (isLastSample && distanceDelta > 100)) {
      const lapTime = s.timestamp - lastLapTime;
      if (lapTime > 0) {
        computedLaps.push({
          lapIndex: currentLapIndex++,
          totalTime: lapTime,
          distance: distanceDelta,
          avgSpeed: segmentSpeedSum / segmentCount,
          avgPower: segmentPowerSum / segmentCount
        });
      }

      lastLapDist = dist;
      lastLapTime = s.timestamp;
      segmentPowerSum = 0;
      segmentSpeedSum = 0;
      segmentCount = 0;
    }
  }

  return computedLaps;
});

// Elevation extremes -- totalAscent/totalDescent/avgGrade come from the
// session doc (Garmin-provided or computed server-side), but min/max
// elevation isn't a session-level aggregate anywhere in the pipeline, so
// it's derived here from the per-sample altitude, same approach kpiStats
// already used for avgPower/avgSpeed below.
const elevationExtremes = computed(() => {
  const samples = props.session.samples || [];
  const altitudes = samples.map(s => s.altitude).filter((a): a is number => typeof a === 'number');
  if (altitudes.length === 0) return { min: null as number | null, max: null as number | null };
  return { min: Math.min(...altitudes), max: Math.max(...altitudes) };
});

// Rolling-window max average over a fixed sample count (~N seconds at the
// pipeline's 1Hz recording assumption, same as computeWeightedAvgPower in
// garminMetrics.ts) -- used for best-effort power/pace windows.
function rollingMaxAverage(values: number[], windowSize: number): number | null {
  if (values.length < windowSize) return null;
  let sum = 0;
  for (let i = 0; i < windowSize; i++) sum += values[i];
  let max = sum;
  for (let i = windowSize; i < values.length; i++) {
    sum += values[i] - values[i - windowSize];
    if (sum > max) max = sum;
  }
  return max / windowSize;
}

// Minimum time (seconds) to cover >= targetDist metres, via a two-pointer
// sliding window over cumulative distance. garminDist is monotonically
// non-decreasing, so this is O(n).
function fastestSplitSeconds(samples: Sample[], targetDist: number): number | null {
  if (samples.length < 2) return null;
  let left = 0;
  let best: number | null = null;
  for (let right = 0; right < samples.length; right++) {
    while (samples[right].garminDist - samples[left].garminDist >= targetDist) {
      const time = samples[right].timestamp - samples[left].timestamp;
      if (time > 0 && (best === null || time < best)) best = time;
      left++;
    }
  }
  return best;
}

// Best-effort running paces (seconds/km), derived client-side from samples
// already on the page -- see plan doc for why this isn't server-side.
const bestEffortPaces = computed(() => {
  const s = effectiveSession.value;
  const samples = props.session.samples || [];
  if (s.sport !== 'running' || samples.length < 2) return null;

  const speeds = samples.map(sm => sm.speed || 0); // km/h
  const best30sSpeed = rollingMaxAverage(speeds, 30);
  const time1km = fastestSplitSeconds(samples, 1000);
  const time5km = fastestSplitSeconds(samples, 5000);

  return {
    best30s: best30sSpeed != null && best30sSpeed > 0 ? 3600 / best30sSpeed : null,
    best1km: time1km,
    best5km: time5km != null ? time5km / 5 : null
  };
});

// Best-effort cycling power curve (watts), same rolling-window technique.
const bestEffortPower = computed(() => {
  const s = effectiveSession.value;
  const samples = props.session.samples || [];
  if (s.sport !== 'cycling' || samples.length < 5) return null;

  const powers = samples.map(sm => sm.power || 0);
  return {
    best5s: rollingMaxAverage(powers, 5),
    best1min: rollingMaxAverage(powers, 60),
    best5min: rollingMaxAverage(powers, 300),
    best20min: rollingMaxAverage(powers, 1200)
  };
});

const DECOUPLING_MIN_DURATION_SEC = 20 * 60;

// Minetti et al. (2002) energy cost of running on a gradient, normalized so
// 0% grade has a cost multiplier of 1.0. Mirrors garminMetrics.ts's
// computeNormalizedGradedPace -- duplicated here since this component is
// deliberately frontend-only (see plan doc), not imported since that file
// only runs in the Functions runtime.
function minettiCostMultiplier(gradeFraction: number): number {
  const g = Math.max(-0.45, Math.min(0.45, gradeFraction));
  const costPerKg =
    155.4 * Math.pow(g, 5) -
    30.4 * Math.pow(g, 4) -
    43.3 * Math.pow(g, 3) +
    46.3 * Math.pow(g, 2) +
    19.5 * g +
    3.6;
  const flatCostPerKg = 3.6;
  return costPerKg / flatCostPerKg;
}

// Centered moving average tolerant of nulls/gaps -- used to smooth the
// per-sample grade signal before it feeds the (nonlinear, 5th-order) Minetti
// curve. Barometric/GPS altitude is noisy sample-to-sample; feeding that
// straight into the cost curve amplifies the noise into a swingy, useless
// grade-adjusted pace. A ~30s window matches the other best-effort windows
// already used in this file and is long enough to average out altimeter
// jitter while still tracking real, sustained hills.
function smoothWithGaps(values: (number | null)[], windowSize: number): (number | null)[] {
  const n = values.length;
  const radius = Math.floor(windowSize / 2);
  const result: (number | null)[] = new Array(n).fill(null);
  for (let i = 0; i < n; i++) {
    let sum = 0;
    let count = 0;
    for (let j = Math.max(0, i - radius); j <= Math.min(n - 1, i + radius); j++) {
      const v = values[j];
      if (v != null) {
        sum += v;
        count++;
      }
    }
    result[i] = count > 0 ? sum / count : null;
  }
  return result;
}

const GRADE_SMOOTHING_WINDOW = 31; // ~30s at 1Hz

// Per-sample grade-adjusted equivalent flat speed (km/h) for running,
// smoothing the grade signal first so a noisy altimeter doesn't dominate the
// Minetti adjustment. Falls back to raw speed sample-by-sample wherever
// grade is unavailable, rather than discarding the whole session.
const gradeAdjustedSpeeds = computed<(number | null)[] | null>(() => {
  const s = effectiveSession.value;
  const samples = props.session.samples || [];
  if (s.sport !== 'running' || samples.length === 0) return null;

  const hasAnyGrade = samples.some(sm => typeof sm.grade === 'number');
  if (!hasAnyGrade) return null;

  const smoothedGrades = smoothWithGaps(samples.map(sm => sm.grade), GRADE_SMOOTHING_WINDOW);

  return samples.map((sm, i) => {
    const speed = sm.speed || 0;
    const grade = smoothedGrades[i];
    if (grade == null) return speed;
    return speed / minettiCostMultiplier(grade / 100);
  });
});

// Aerobic decoupling: split the session at the time midpoint, compare
// Speed:HR (running, grade-adjusted where possible) / Power:HR (cycling)
// between halves. Only meaningful for sustained steady efforts, so gated on
// >=20 min total duration.
const aerobicDecoupling = computed<number | null>(() => {
  const s = effectiveSession.value;
  const samples = props.session.samples || [];
  if (samples.length < 4) return null;

  const totalDuration = samples[samples.length - 1].timestamp - samples[0].timestamp;
  if (totalDuration < DECOUPLING_MIN_DURATION_SEC) return null;

  const gradeAdjusted = gradeAdjustedSpeeds.value;
  const mid = Math.floor(samples.length / 2);
  const firstHalf = samples.slice(0, mid);
  const secondHalf = samples.slice(mid);
  const firstHalfNums = gradeAdjusted?.slice(0, mid) ?? null;
  const secondHalfNums = gradeAdjusted?.slice(mid) ?? null;

  const halfRatio = (half: Sample[], nums: (number | null)[] | null): number | null => {
    let numSum = 0;
    let hrSum = 0;
    let hrCount = 0;
    half.forEach((sm, i) => {
      const num = s.sport === 'cycling' ? sm.power : (nums ? nums[i] : sm.speed);
      numSum += num || 0;
      if (sm.heartRate) {
        hrSum += sm.heartRate;
        hrCount++;
      }
    });
    if (hrCount === 0) return null;
    const avgHr = hrSum / hrCount;
    const avgNum = numSum / half.length;
    return avgHr > 0 ? avgNum / avgHr : null;
  };

  const ratio1 = halfRatio(firstHalf, firstHalfNums);
  const ratio2 = halfRatio(secondHalf, secondHalfNums);
  if (ratio1 == null || ratio2 == null || ratio1 === 0) return null;

  return ((ratio1 - ratio2) / ratio1) * 100;
});

// LeGizmo's own transparent linear mapping of decoupling -> a 0-100
// "durability" score, NOT a standardized industry formula (the durability
// research treats decoupling as the measure, not a fixed score). 0%
// decoupling -> 100, 20%+ decoupling -> 0; Friel's <5%-is-good guidance
// lands around 75+.
const durabilityScore = computed<number | null>(() => {
  const decoupling = aerobicDecoupling.value;
  if (decoupling == null) return null;
  const score = 100 - (decoupling / 20) * 100;
  return Math.round(Math.min(100, Math.max(0, score)));
});

// Single-session absolute efficiency (output per heartbeat) -- unlike
// decoupling, this isn't about drift within the session.
const efficiencyFactor = computed<number | null>(() => {
  const s = effectiveSession.value;
  const samples = props.session.samples || [];
  if (samples.length === 0 || !s.avgHeartRate) return null;

  if (s.sport === 'cycling') {
    const power = s.weightedAvgPower ?? (samples.reduce((sum, sm) => sum + (sm.power || 0), 0) / samples.length);
    return power > 0 ? power / s.avgHeartRate : null;
  }
  if (s.sport === 'running') {
    const paceSecPerKm = s.normalizedGradedPace ?? null;
    const avgSpeedKmh = samples.reduce((sum, sm) => sum + (sm.speed || 0), 0) / samples.length;
    const speedMs = paceSecPerKm && paceSecPerKm > 0 ? 1000 / paceSecPerKm : avgSpeedKmh / 3.6;
    return speedMs > 0 ? speedMs / s.avgHeartRate : null;
  }
  return null;
});

const variabilityIndex = computed<number | null>(() => {
  const s = effectiveSession.value;
  const samples = props.session.samples || [];
  if (s.sport !== 'cycling' || samples.length === 0 || !s.weightedAvgPower) return null;
  const avgPower = samples.reduce((sum, sm) => sum + (sm.power || 0), 0) / samples.length;
  return avgPower > 0 ? s.weightedAvgPower / avgPower : null;
});

interface MetricRow {
  label: string;
  value: string;
  source?: MetricSource;
}

interface MetricGroup {
  title: string;
  icon: string;
  color: string;
  rows: MetricRow[];
}

// Metric groups, translating Garmin Connect's own Stats-tab grouping
// (Distance/Timing/Power/Elevation/etc.) into LeGizmo's card idiom. Each row
// is tagged with its metricsSource (garmin.ts / garminMetrics.ts) where
// applicable, so the chip in the template can label Garmin-provided vs.
// LeGizmo-estimated values.
const metricGroups = computed<MetricGroup[]>(() => {
  const s = effectiveSession.value;
  const source = s.metricsSource || {};
  const samples = props.session.samples || [];

  // `distance` (metres) is always written for Garmin imports. totalMass is
  // the rider + bike mass, so falling back to it produced a bogus distance.
  const rawDist = props.session.distance || 0;
  const distKm = (rawDist / 1000).toFixed(2);

  let durationSec = 0;
  if (samples.length > 1) {
    durationSec = samples[samples.length - 1].timestamp - samples[0].timestamp;
  }

  let avgPower = 0;
  let avgSpeed = 0;
  let maxPower = 0;
  let avgHR = 0;

  if (samples.length > 0) {
    let powerSum = 0;
    let speedSum = 0;
    let hrSum = 0;
    let hrCount = 0;

    samples.forEach(sample => {
      powerSum += sample.power || 0;
      speedSum += sample.speed || 0;
      if (sample.power > maxPower) maxPower = sample.power;
      if (sample.heartRate) {
        hrSum += sample.heartRate;
        hrCount++;
      }
    });

    avgPower = Math.round(powerSum / samples.length);
    avgSpeed = speedSum / samples.length;
    avgHR = hrCount > 0 ? Math.round(hrSum / hrCount) : 0;
  }

  const elev = elevationExtremes.value;
  const groups: MetricGroup[] = [];

  const distanceTimeRows: MetricRow[] = [
    { label: 'Distance', value: `${distKm} km` },
    { label: 'Elapsed Time', value: formatDuration(s.elapsedTime ?? durationSec), source: source.elapsedTime }
  ];
  if (s.movingTime != null) {
    distanceTimeRows.push({ label: 'Moving Time', value: formatDuration(s.movingTime), source: source.movingTime });
  }
  if (s.calories != null) {
    distanceTimeRows.push({ label: 'Calories', value: `${Math.round(s.calories)} kcal`, source: source.calories });
  }
  groups.push({ title: 'Distance & Time', icon: 'mdi-map-marker-distance', color: 'primary', rows: distanceTimeRows });

  const speedRows: MetricRow[] = [{ label: 'Avg Speed', value: `${avgSpeed.toFixed(1)} km/h` }];
  if (s.maxSpeed != null) {
    speedRows.push({ label: 'Max Speed', value: `${s.maxSpeed.toFixed(1)} km/h`, source: source.maxSpeed });
  }
  groups.push({ title: 'Speed', icon: 'mdi-speedometer', color: 'green-darken-2', rows: speedRows });

  const powerRows: MetricRow[] = [
    { label: 'Avg Power', value: `${avgPower} W` },
    { label: 'Max Power', value: `${maxPower} W` }
  ];
  if (s.weightedAvgPower != null) {
    powerRows.push({ label: 'Weighted Avg Power', value: `${Math.round(s.weightedAvgPower)} W`, source: source.weightedAvgPower });
  }
  if (s.trainingLoad != null) {
    powerRows.push({ label: 'Training Load', value: Math.round(s.trainingLoad).toString(), source: source.trainingLoad });
  }
  if (s.intensityRatio != null) {
    powerRows.push({ label: 'Intensity Ratio', value: s.intensityRatio.toFixed(2), source: source.intensityRatio });
  }
  groups.push({ title: 'Power', icon: 'mdi-flash', color: 'orange-darken-2', rows: powerRows });

  const elevationRows: MetricRow[] = [];
  if (s.totalAscent != null) {
    elevationRows.push({ label: 'Total Ascent', value: `${Math.round(s.totalAscent)} m`, source: source.totalAscent });
  }
  if (s.totalDescent != null) {
    elevationRows.push({ label: 'Total Descent', value: `${Math.round(s.totalDescent)} m`, source: source.totalDescent });
  }
  if (s.avgGrade != null) {
    elevationRows.push({ label: 'Avg Grade', value: `${s.avgGrade.toFixed(1)}%`, source: source.avgGrade });
  }
  if (elev.min != null && elev.max != null) {
    elevationRows.push({ label: 'Min / Max Elevation', value: `${Math.round(elev.min)} m / ${Math.round(elev.max)} m` });
  }
  if (elevationRows.length > 0) {
    groups.push({ title: 'Elevation', icon: 'mdi-image-filter-hdr', color: 'brown-darken-1', rows: elevationRows });
  }

  const hrRows: MetricRow[] = [];
  if (avgHR > 0) {
    hrRows.push({ label: 'Avg Heart Rate', value: `${avgHR} bpm` });
    if (s.maxHeartRate != null) hrRows.push({ label: 'Max Heart Rate', value: `${Math.round(s.maxHeartRate)} bpm`, source: source.maxHeartRate });
    if (s.minHeartRate != null) hrRows.push({ label: 'Min Heart Rate', value: `${Math.round(s.minHeartRate)} bpm`, source: source.minHeartRate });
  }
  groups.push({
    title: 'Heart Rate',
    icon: 'mdi-heart',
    color: 'red',
    rows: hrRows.length > 0 ? hrRows : [{ label: 'Heart Rate', value: 'No monitor connected' }]
  });

  if (s.avgCadence != null) {
    const cadenceRows: MetricRow[] = [{ label: 'Avg Cadence', value: `${Math.round(s.avgCadence)} rpm`, source: source.avgCadence }];
    if (s.maxCadence != null) {
      cadenceRows.push({ label: 'Max Cadence', value: `${Math.round(s.maxCadence)} rpm`, source: source.maxCadence });
    }
    groups.push({ title: 'Cadence', icon: 'mdi-rotate-right', color: 'green-darken-2', rows: cadenceRows });
  }

  if (s.sport === 'running') {
    const paces = bestEffortPaces.value;
    const paceRows: MetricRow[] = [
      { label: 'Avg Pace', value: avgSpeed > 0 ? `${formatPaceFromSeconds(3600 / avgSpeed)} /km` : '--' }
    ];
    if (s.normalizedGradedPace != null) {
      paceRows.push({ label: 'Normalized Graded Pace', value: `${formatPaceFromSeconds(s.normalizedGradedPace)} /km`, source: source.normalizedGradedPace });
    }
    if (paces?.best30s != null) paceRows.push({ label: 'Best 30s', value: `${formatPaceFromSeconds(paces.best30s)} /km` });
    if (paces?.best1km != null) paceRows.push({ label: 'Best 1km', value: `${formatPaceFromSeconds(paces.best1km)} /km` });
    if (paces?.best5km != null) paceRows.push({ label: 'Best 5km', value: `${formatPaceFromSeconds(paces.best5km)} /km` });
    groups.push({ title: 'Pace', icon: 'mdi-run-fast', color: 'indigo-darken-1', rows: paceRows });
  }

  const decoupling = aerobicDecoupling.value;
  const durability = durabilityScore.value;
  const efficiency = efficiencyFactor.value;
  const variability = variabilityIndex.value;
  if (decoupling != null || efficiency != null) {
    const performanceRows: MetricRow[] = [];
    if (decoupling != null) {
      performanceRows.push({ label: 'Aerobic Decoupling', value: `${decoupling.toFixed(1)}%` });
    }
    if (durability != null) {
      performanceRows.push({ label: 'Durability Score', value: `${durability} / 100` });
    }
    if (efficiency != null) {
      performanceRows.push({ label: 'Efficiency Factor', value: efficiency.toFixed(2) });
    }
    if (variability != null) {
      performanceRows.push({ label: 'Variability Index', value: variability.toFixed(2) });
    }
    groups.push({ title: 'Performance', icon: 'mdi-chart-bell-curve', color: 'deep-purple', rows: performanceRows });
  }

  const power = bestEffortPower.value;
  if (power && (power.best5s != null || power.best1min != null || power.best5min != null || power.best20min != null)) {
    const powerCurveRows: MetricRow[] = [];
    if (power.best5s != null) powerCurveRows.push({ label: 'Best 5s', value: `${Math.round(power.best5s)} W` });
    if (power.best1min != null) powerCurveRows.push({ label: 'Best 1min', value: `${Math.round(power.best1min)} W` });
    if (power.best5min != null) powerCurveRows.push({ label: 'Best 5min', value: `${Math.round(power.best5min)} W` });
    if (power.best20min != null) powerCurveRows.push({ label: 'Best 20min', value: `${Math.round(power.best20min)} W` });
    groups.push({ title: 'Power Curve', icon: 'mdi-chart-line-variant', color: 'orange-darken-3', rows: powerCurveRows });
  }

  return groups;
});

// Telemetry Timeline Graph configuration
const telemetryData = computed(() => {
  const samples = props.session.samples || [];
  
  // Downsample to ~200 points for chart performance if ride is long
  const skip = Math.max(1, Math.floor(samples.length / 200));
  const data: Array<{ dist: number; power: number; speed: number; hr: number | null }> = [];

  for (let i = 0; i < samples.length; i += skip) {
    const s = samples[i];
    data.push({
      dist: Number(((s.garminDist || 0) / 1000).toFixed(2)),
      power: s.power || 0,
      speed: Number(s.speed.toFixed(1)),
      hr: s.heartRate
    });
  }

  return data;
});

const chartSeries = computed(() => {
  const data = telemetryData.value;
  return [
    {
      name: 'Power (W)',
      type: 'line',
      data: data.map(d => ({ x: d.dist, y: d.power }))
    },
    {
      name: 'Speed (km/h)',
      type: 'line',
      data: data.map(d => ({ x: d.dist, y: d.speed }))
    },
    {
      name: 'Heart Rate (bpm)',
      type: 'line',
      data: data.map(d => ({ x: d.dist, y: d.hr }))
    }
  ];
});

const chartOptions = computed(() => {
  const samples = props.session.samples || [];
  const hasHR = samples.some(s => s.heartRate !== null);

  return {
    chart: {
      type: 'line',
      height: 350,
      toolbar: { show: false },
      zoom: { enabled: false }
    },
    stroke: {
      width: [2, 2, 2],
      curve: 'smooth'
    },
    colors: ['#ff9800', '#2196f3', '#e91e63'],
    xaxis: {
      type: 'numeric',
      title: {
        text: 'Distance (km)',
        style: { fontWeight: 'bold' }
      },
      labels: {
        formatter: (val: number) => `${Number(val).toFixed(1)} km`
      }
    },
    yaxis: [
      {
        title: { text: 'Power (W)' },
        labels: { formatter: (val: number) => `${Math.round(val)} W` }
      },
      {
        opposite: true,
        title: { text: 'Speed (km/h)' },
        labels: { formatter: (val: number) => `${Number(val).toFixed(1)}` }
      }
    ],
    tooltip: {
      shared: true,
      intersect: false,
      x: { formatter: (val: number) => `${Number(val).toFixed(2)} km` }
    },
    legend: {
      position: 'top',
      horizontalAlign: 'right'
    }
  };
});

// Leaflet Map Initialization
const initMap = () => {
  const samples = props.session.samples || [];
  const coordinates = samples
    .filter(s => s.lat !== null && s.lng !== null)
    .map(s => [s.lat, s.lng] as [number, number]);

  if (coordinates.length === 0) {
    hasCoordinates.value = false;
    return;
  }

  hasCoordinates.value = true;

  // Make sure map container exists before initializing
  const container = document.getElementById('garmin-map');
  if (!container) return;

  // Initialize Leaflet Map
  map = L.map('garmin-map', {
    zoomControl: true,
    scrollWheelZoom: false
  });

  // Load OpenStreetMap tiles
  L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
    attribution: '© OpenStreetMap contributors'
  }).addTo(map);

  // Draw Polyline route
  const polyline = L.polyline(coordinates, {
    color: '#ff4400',
    weight: 4,
    opacity: 0.9
  }).addTo(map);

  // Fit bounds to polyline
  map.fitBounds(polyline.getBounds(), { padding: [20, 20] });

  // Add Start node (solid green circle)
  L.circleMarker(coordinates[0], {
    radius: 6,
    fillColor: '#2e7d32',
    fillOpacity: 1,
    color: '#ffffff',
    weight: 2
  }).addTo(map).bindPopup('Start');

  // Add End node (solid red circle)
  L.circleMarker(coordinates[coordinates.length - 1], {
    radius: 6,
    fillColor: '#c62828',
    fillOpacity: 1,
    color: '#ffffff',
    weight: 2
  }).addTo(map).bindPopup('Finish');
};

onMounted(() => {
  // Inject Leaflet CSS dynamically from unpkg CDN to bypass Vite asset bundling bugs
  const linkId = 'leaflet-cdn-css';
  if (!document.getElementById(linkId)) {
    const link = document.createElement('link');
    link.id = linkId;
    link.rel = 'stylesheet';
    link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
    document.head.appendChild(link);
  }

  nextTick(() => {
    setTimeout(() => {
      initMap();
    }, 200);
  });
});
</script>

<style scoped>
.relative {
  position: relative;
}
.absolute-center {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}
#garmin-map :deep(.leaflet-control-attribution) {
  font-size: 8px !important;
}
</style>
